import { useEffect, useMemo, useState } from 'react'
import * as UserHttp from '../../services/api/https/settings/user.http'
import { saveToken } from '../../services/localstorage/localstorage.service'
import { errorHandler } from '../../services/util/error.service'

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  LOGIN: 'Validando informações...',
  MFA_PENDING: 'MFA_PENDING',
  MFA_VERIFY: 'Validando código...',
  MFA_RESEND: 'Reenviando código...',
  SUCCESS: 'Sucesso',
}

export const MFA_CODE_LENGTH = 6

// Erros de MFA que encerram a etapa de código e obrigam um novo login
const MFA_FATAL_REASONS = ['INVALID_CHALLENGE', 'EXPIRED', 'BLOCKED', 'TOO_MANY', 'EMAIL_FAILED']

const VALIDATE_MESSAGE = {
  EMAIL: 'Email vazio ou incorreto',
  PASSWORD: 'Senha vazia ou incorreta',
  CODE: `Informe o código de ${MFA_CODE_LENGTH} dígitos enviado ao seu e-mail`,
}

const createInitialMfa = () => ({
  challengeId: null,
  emailMask: '',
  code: '',
  expiresAt: null,
  resendAvailableAt: null,
  now: Date.now(),
})

const createInitialState = () => ({
  userCredentials: {
    email: '',
    password: '',
  },
  mfa: createInitialMfa(),
  loadingMessage: LOAD_MESSAGE.NONE,
  isLoading: false,
  errorMessage: null,
})

const normalizeError = error => {
  if (error && typeof error === 'object' && error.reason) {
    return { message: error.error || errorHandler(error), reason: error.reason, ...error }
  }
  return { message: errorHandler(error), reason: null }
}

const sanitizeCode = value =>
  String(value || '')
    .replace(/\D/g, '')
    .slice(0, MFA_CODE_LENGTH)

const secondsUntil = (target, now) => (target ? Math.max(Math.ceil((target - now) / 1000), 0) : 0)

const buildMfaFromResponse = (prevMfa, response) => {
  const now = Date.now()
  return {
    ...prevMfa,
    challengeId: response.challenge_id,
    emailMask: response.email_mask,
    code: '',
    expiresAt: now + response.expires_in * 1000,
    resendAvailableAt: now + response.resend_cooldown * 1000,
    now,
  }
}

export default function useLogin() {
  const [state, setState] = useState(createInitialState)

  const isMfaStep = state.mfa.challengeId !== null

  useEffect(() => {
    if (!isMfaStep) return undefined
    const timer = setInterval(() => {
      setState(prevState => ({ ...prevState, mfa: { ...prevState.mfa, now: Date.now() } }))
    }, 1000)
    return () => clearInterval(timer)
  }, [isMfaStep])

  const localDispatch = useMemo(() => {
    //Helper
    const userCredentialIsValid = (credential, errorRelative) => {
      if (!credential || !credential.trim()) {
        setState(prevState => ({
          ...prevState,
          errorMessage: { message: errorRelative, reason: null },
        }))
        return false
      }
      return true
    }

    const backToLoginState = prevState => ({
      ...createInitialState(),
      userCredentials: { email: prevState.userCredentials.email, password: '' },
    })

    return {
      // Actions
      requestUserLogin(userCredentials) {
        if (
          userCredentialIsValid(userCredentials.email, VALIDATE_MESSAGE.EMAIL) &&
          userCredentialIsValid(userCredentials.password, VALIDATE_MESSAGE.PASSWORD)
        ) {
          UserHttp.loginUser(userCredentials)
            .then(response => {
              if (typeof response === 'string') {
                saveToken(response)
                setState(prevState => ({
                  ...prevState,
                  loadingMessage: LOAD_MESSAGE.SUCCESS,
                  isLoading: false,
                }))
                return
              }
              if (response && response.mfa_required) {
                setState(prevState => ({
                  ...prevState,
                  userCredentials: { ...prevState.userCredentials, password: '' },
                  mfa: buildMfaFromResponse(prevState.mfa, response),
                  loadingMessage: LOAD_MESSAGE.MFA_PENDING,
                  isLoading: false,
                }))
                return
              }
              throw new Error('Resposta inesperada do servidor ao efetuar login')
            })
            .catch(error =>
              setState(prevState => ({ ...prevState, errorMessage: normalizeError(error) })),
            )
        }
      },

      verifyMfaCode(mfa) {
        if (mfa.code.length !== MFA_CODE_LENGTH) {
          setState(prevState => ({
            ...prevState,
            errorMessage: { message: VALIDATE_MESSAGE.CODE, reason: 'VALIDATION' },
          }))
          return
        }
        UserHttp.verifyMfaCode({ challenge_id: mfa.challengeId, code: mfa.code })
          .then(token => {
            saveToken(token)
            setState(prevState => ({
              ...prevState,
              loadingMessage: LOAD_MESSAGE.SUCCESS,
              isLoading: false,
            }))
          })
          .catch(error =>
            setState(prevState => ({ ...prevState, errorMessage: normalizeError(error) })),
          )
      },

      resendMfaCode(mfa) {
        UserHttp.resendMfaCode(mfa.challengeId)
          .then(response =>
            setState(prevState => ({
              ...prevState,
              mfa: buildMfaFromResponse(prevState.mfa, response),
              loadingMessage: LOAD_MESSAGE.MFA_PENDING,
              isLoading: false,
            })),
          )
          .catch(error => {
            const normalized = normalizeError(error)
            setState(prevState => ({
              ...prevState,
              mfa:
                normalized.reason === 'COOLDOWN' && normalized.retry_after
                  ? {
                      ...prevState.mfa,
                      resendAvailableAt: Date.now() + normalized.retry_after * 1000,
                    }
                  : prevState.mfa,
              errorMessage: normalized,
            }))
          })
      },

      onErrorShown() {
        setState(prevState => {
          const inMfaStep = prevState.mfa.challengeId !== null
          const reason = prevState.errorMessage ? prevState.errorMessage.reason : null
          if (inMfaStep && MFA_FATAL_REASONS.includes(reason)) {
            return backToLoginState(prevState)
          }
          if (inMfaStep) {
            const clearCode = reason === 'INVALID_CODE' || reason === 'VALIDATION'
            return {
              ...prevState,
              errorMessage: null,
              isLoading: false,
              loadingMessage: LOAD_MESSAGE.MFA_PENDING,
              mfa: { ...prevState.mfa, code: clearCode ? '' : prevState.mfa.code },
            }
          }
          return createInitialState()
        })
      },

      resetState() {
        setState(createInitialState())
      },

      backToLogin() {
        setState(prevState => backToLoginState(prevState))
      },

      // Events
      onAcessarClicked() {
        setState(prevState => ({
          ...prevState,
          loadingMessage: LOAD_MESSAGE.LOGIN,
          isLoading: true,
        }))
      },

      onChange(name, value) {
        setState(prevState => ({
          ...prevState,
          userCredentials: { ...prevState.userCredentials, [name]: value },
        }))
      },

      onMfaCodeChange(value) {
        const code = sanitizeCode(value)
        setState(prevState => {
          const expired = secondsUntil(prevState.mfa.expiresAt, Date.now()) === 0
          const autoSubmit = code.length === MFA_CODE_LENGTH && !prevState.isLoading && !expired
          return {
            ...prevState,
            mfa: { ...prevState.mfa, code },
            loadingMessage: autoSubmit ? LOAD_MESSAGE.MFA_VERIFY : prevState.loadingMessage,
            isLoading: autoSubmit ? true : prevState.isLoading,
          }
        })
      },

      onConfirmarCodigoClicked() {
        setState(prevState => {
          const expired = secondsUntil(prevState.mfa.expiresAt, Date.now()) === 0
          if (prevState.isLoading || expired) return prevState
          return { ...prevState, loadingMessage: LOAD_MESSAGE.MFA_VERIFY, isLoading: true }
        })
      },

      onReenviarCodigoClicked() {
        setState(prevState => {
          const inCooldown = secondsUntil(prevState.mfa.resendAvailableAt, Date.now()) > 0
          if (prevState.isLoading || inCooldown) return prevState
          return { ...prevState, loadingMessage: LOAD_MESSAGE.MFA_RESEND, isLoading: true }
        })
      },
    }
  }, [])

  const secondsToExpire = secondsUntil(state.mfa.expiresAt, state.mfa.now)
  const secondsToResend = secondsUntil(state.mfa.resendAvailableAt, state.mfa.now)

  const mfaInfo = {
    isMfaStep,
    secondsToExpire,
    secondsToResend,
    isExpired: isMfaStep && secondsToExpire === 0,
    canResend: isMfaStep && secondsToResend === 0 && !state.isLoading,
  }

  return { state, localDispatch, mfaInfo }
}
